// \src\pages\empresa\page.tsx

import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useAdmin } from "@/context/AdminContext";
import PromoCarouselHome from "@/pages/home/components/PromoCarouselHome";

function isVideoMedia(src: string): boolean {
  return /\.(mp4|webm)(?:[?#].*)?$/i.test(String(src || ""));
}

const values = [
  { icon: "ri-heart-line", title: "Compromisso", description: "Comprometidos com a satisfação total dos nossos clientes em cada atendimento." },
  { icon: "ri-shield-star-line", title: "Qualidade", description: "Trabalhamos apenas com produtos das melhores marcas e procedências certificadas." },
  { icon: "ri-links-line", title: "Honestidade", description: "Relações transparentes com clientes, fornecedores e colaboradores." },
  { icon: "ri-leaf-line", title: "Responsabilidade", description: "Comprometidos com práticas sustentáveis e responsabilidade socioambiental." },
  { icon: "ri-rocket-line", title: "Inovação", description: "Sempre atualizados com as novidades do setor para oferecer o que há de melhor." },
  { icon: "ri-team-line", title: "Pessoas", description: "Nossa equipe é nosso maior patrimônio. Valorizamos cada colaborador." },
];

export default function Empresa() {
  const { data } = useAdmin();
  const company = data.companyInfo;
  const headerMedia = data.settings.companyHeaderMedia || "/images/empresa/01.jpg";
  const timeline = [...data.companyTimeline].sort(
    (a, b) =>
      a.sortOrder - b.sortOrder ||
      a.eventYear - b.eventYear ||
      a.id - b.id,
  );

  return (
    <>
      <Navbar />

      <main className="pt-20 min-h-screen bg-background-50">
        <section className="relative h-[300px] md:h-[380px]">
          {isVideoMedia(headerMedia) ? (
            <video
              src={headerMedia}
              className="w-full h-full object-cover object-top"
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              aria-label="M&Z Empresa"
            />
          ) : (
            <img
              src={headerMedia}
              alt="M&Z Empresa"
              className="w-full h-full object-cover object-top"
            />
          )}

          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70" />

          <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
            <nav className="text-xs text-background-400 font-label mb-4">
              <Link
                to="/"
                className="hover:text-primary-400 cursor-pointer"
              >
                Início
              </Link>

              <span className="mx-2">/</span>
              <span className="text-primary-400">A Empresa</span>
            </nav>

            <h1 className="text-4xl md:text-5xl font-heading font-bold text-background-50 mb-3">
              A Empresa
            </h1>

            <p className="text-background-300 text-base max-w-xl">
              Conheça nossa história, missão e os valores que guiam cada passo da M&amp;Z
            </p>
          </div>
        </section>

        <PromoCarouselHome />

        <section className="max-w-7xl mx-auto px-4 md:px-8 py-16">
          <div className="flex flex-col lg:flex-row gap-12 items-start">
            <div className="flex-1">
              <span className="inline-block bg-primary-100 text-primary-700 text-xs font-label font-semibold uppercase tracking-widest px-3 py-1 rounded-full mb-4">
                Nossa História
              </span>

              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-5 leading-tight">
                {company.historyTitle}
              </h2>

              <div className="space-y-4 text-foreground-600 text-sm leading-relaxed whitespace-pre-wrap">
                {company.historyText.split("\n\n").map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </div>

            <div className="w-full lg:w-96 shrink-0">
              <img
                src="/images/empresa/02.jpg"
                alt="M&Z Materiais"
                className="w-full h-80 lg:h-96 object-cover object-top rounded-lg border border-background-200"
              />
            </div>
          </div>
        </section>

        <section className="bg-foreground-950 py-16 px-4 md:px-8">
          <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: "ri-focus-3-line", title: "Missão", text: company.mission },
              { icon: "ri-eye-line", title: "Visão", text: company.vision },
              { icon: "ri-award-line", title: "Meta", text: company.meta },
            ].map((item) => (
              <div key={item.title} className="text-center">
                <div className="w-14 h-14 flex items-center justify-center bg-primary-500 text-background-50 rounded-full mx-auto mb-4">
                  <i className={`${item.icon} text-2xl`} />
                </div>

                <h3 className="text-background-50 font-heading font-bold text-xl mb-3">
                  {item.title}
                </h3>

                <p className="text-background-400 text-sm leading-relaxed">
                  {item.text}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 md:px-8 py-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-3">
              Nossa Trajetória
            </h2>

            <div className="w-16 h-1 bg-primary-500 mx-auto" />
          </div>

          <div className="relative">
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-background-200 hidden md:block" />

            <div className="space-y-8">
              {timeline.map((item, i) => (
                <div
                  key={item.id}
                  className={`flex flex-col md:flex-row items-start md:items-center gap-4 ${
                    i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                  }`}
                >
                  <div
                    className={`flex-1 ${
                      i % 2 === 0 ? "md:text-right" : "md:text-left"
                    }`}
                  >
                    <div
                      className={`bg-background-50 border border-background-200 rounded-lg p-5 hover:border-primary-200 transition-all ${
                        i % 2 === 0 ? "md:mr-8" : "md:ml-8"
                      }`}
                    >
                      <h3 className="font-heading font-bold text-foreground-950 text-lg">
                        {item.title}
                      </h3>

                      <p className="text-foreground-600 text-sm mt-1">
                        {item.description}
                      </p>
                    </div>
                  </div>

                  <div className="hidden md:flex w-10 h-10 items-center justify-center bg-primary-500 text-background-50 rounded-full shrink-0 z-10" />

                  <div className="flex-1">
                    <span
                      className={`block font-heading font-bold text-primary-600 text-base ${
                        i % 2 === 0
                          ? "md:ml-8"
                          : "md:mr-8 md:text-right"
                      }`}
                    >
                      {item.eventYear}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-background-100 py-16 px-4 md:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-3">
                Nossos Valores
              </h2>

              <p className="text-foreground-600 text-sm max-w-lg mx-auto">
                Princípios que guiam nossa empresa há mais de{" "}
                {company.foundedYear
                  ? new Date().getFullYear() - company.foundedYear
                  : 15}{" "}
                anos
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
              {values.map((v, i) => (
                <div
                  key={i}
                  className="bg-background-50 border border-background-200 rounded-lg p-5 hover:border-primary-200 transition-all"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-100 text-primary-600 rounded-lg mb-3">
                    <i className={`${v.icon} text-lg`} />
                  </div>

                  <h3 className="font-heading font-semibold text-foreground-950 mb-2">
                    {v.title}
                  </h3>

                  <p className="text-foreground-600 text-xs leading-relaxed">
                    {v.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 px-4 text-center bg-primary-500">
          <h2 className="text-3xl font-heading font-bold text-background-50 mb-3">
            Faça Parte da Nossa História
          </h2>

          <p className="text-primary-100 text-base mb-6 max-w-md mx-auto">
            Junte-se a milhares de clientes que confiam na M&amp;Z para suas obras e reformas.
          </p>

          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to="/contato"
              className="bg-background-50 text-primary-600 hover:bg-background-100 px-8 py-3 rounded-md font-label font-bold text-sm cursor-pointer whitespace-nowrap transition-all"
            >
              Fale Conosco
            </Link>

            <Link
              to="/trabalhe-conosco"
              className="border border-background-50 text-background-50 hover:bg-background-50/10 px-8 py-3 rounded-md font-label font-semibold text-sm cursor-pointer whitespace-nowrap transition-all"
            >
              Trabalhe Conosco
            </Link>
          </div>
        </section>

        {company.construseteActive && (
          <section className="bg-background-100 border-t border-background-200 py-14 md:py-16 px-4 md:px-8">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-8 lg:gap-12 items-center">
                {company.construseteLogo && (
                  <div className="flex justify-center lg:justify-start">
                    {company.construseteUrl ? (
                      <a
                        href={company.construseteUrl}
                        target="_blank"
                        rel="nofollow noreferrer"
                        className="block w-full max-w-[442px] cursor-pointer"
                        aria-label={company.construseteTitle || "Grupo ConstruSete"}
                      >
                        <img
                          src={company.construseteLogo}
                          alt={company.construseteTitle || "Grupo ConstruSete"}
                          className="w-full h-auto object-contain rounded-lg"
                        />
                      </a>
                    ) : (
                      <img
                        src={company.construseteLogo}
                        alt={company.construseteTitle || "Grupo ConstruSete"}
                        className="w-full max-w-[442px] h-auto object-contain rounded-lg"
                      />
                    )}
                  </div>
                )}

                <div className="text-center lg:text-left">
                  {company.construseteBadge && (
                    <span className="inline-block bg-primary-100 text-primary-700 text-xs font-label font-semibold uppercase tracking-widest px-3 py-1 rounded-full mb-4">
                      {company.construseteBadge}
                    </span>
                  )}

                  {company.construseteTitle && (
                    <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                      {company.construseteTitle}
                    </h2>
                  )}

                  {company.construseteText && (
                    <p className="text-foreground-600 text-sm md:text-base leading-relaxed max-w-2xl mx-auto lg:mx-0 whitespace-pre-wrap">
                      {company.construseteText}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}